"use client";
import { motion } from "framer-motion";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

import Fuse from "fuse.js";
import { IoIosSettings } from "react-icons/io";
import { FaRegImage } from "react-icons/fa6";
import {
    RiAccountBoxFill,
    RiAlarmLine,
    RiEmojiStickerLine,
    RiEmotionHappyLine,
    RiFileCopyLine,
    RiLink,
    RiMailLine,
    RiQrCodeLine,
    RiSearchLine,
    RiShareLine,
} from "react-icons/ri";

import { useLeftSideBarStore } from "~/state/store";
import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import { checkIfIsProfileEvent } from "~/app/events/[slug]/actions";

import EventJourneyStatusDropdown from "../events-components/EventJourneyStatusDropdown";
import SubNavigationGroup from "../navigation/SubNavigationGroupOld";

import { getShortLink } from "~/app/shortLinks/actions";
import { useBreakpointV3 } from "~/hooks/useIsMobile";
import { APP_URL } from "~/utils/constants";
import { Input } from "../themed/Input";
import { useQuery } from "@tanstack/react-query";

// fine duplicating this since our whole app only has 2 sidebars and I don't plan
// on reusing this code for any future sidebars
type SidebarItem = {
    name: string;
    icon: React.ReactNode;
    href?: string;
    onClick?: () => void;
    showForAdmin?: boolean;
};

type SidebarSection = {
    title: string;
    items: SidebarItem[];
};

type SidebarItems = {
    [key: string]: SidebarSection;
};

const EventViewSidebar = () => {
    const { setSideBarState, setShareModalOpen } = useLeftSideBarStore();
    const isDesktop = useBreakpointV3("md");

    const sidebarRef = useRef<HTMLDivElement>(null);

    const params = useParams();
    const slug = params?.slug;
    if (!slug) {
        return null;
    }

    const basePath = "/events";
    const pathSlug = Array.isArray(slug) ? slug[0] : slug;

    const { data: permissions } = api.permissions.getUserPermissions.useQuery({
        adminCheck: true,
        event: { slug: (slug as string) ?? "" },
        community: { fromEventSlug: (slug as string) ?? "" },
    });

    const { data: isProfileEvent } = useQuery(
        ["isProfileEvent", { pathSlug }],
        () => checkIfIsProfileEvent(pathSlug as string),
        {
            enabled: !!pathSlug,
        },
    );

    const [isNavigatorShareAvailable, setIsNavigatorShareAvailable] =
        useState(false);
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);

    useEffect(() => {
        setIsNavigatorShareAvailable(!!navigator && !!navigator.share);
    }, []);

    useEffect(() => {
        if (
            permissions?.isManager ||
            permissions?.isAdmin ||
            permissions?.isHost
        ) {
            setIsSidebarVisible(true);
        } else setIsSidebarVisible(false);
    }, [permissions]);

    const SIDEBAR_ITEMS = {
        event: {
            title: "Event",
            items: [
                {
                    name: "View / Edit event",
                    icon: <RiLink />,
                    href: `/events/${pathSlug}`,
                },
                {
                    name: "Settings",
                    icon: <IoIosSettings />,
                    href: `${basePath}/${pathSlug}/${ESidebarItems.SETTINGS}`,
                },
                {
                    name: "Digital badge",
                    icon: <RiEmojiStickerLine />,
                    href: `${basePath}/${pathSlug}/${ESidebarItems.DIGITAL_BADGE}`,
                },
                {
                    name: "Duplicate event",
                    icon: <RiFileCopyLine />,
                    href: `${basePath}/${pathSlug}/${ESidebarItems.DUPLICATE}`,
                },
                ...(isProfileEvent
                    ? [
                          {
                              name: "Add Image Overlay",
                              icon: <FaRegImage size={12} />,
                              href: `${basePath}/${pathSlug}/${ESidebarItems.IMAGE_OVERLAY}`,
                          },
                      ]
                    : []),
            ],
        },
        people: {
            title: "People",
            items: [
                {
                    name: "Attendees",
                    icon: <RiAccountBoxFill />,
                    href: `${basePath}/${pathSlug}/attendees`,
                },
                {
                    name: "Hosts",
                    icon: <RiEmotionHappyLine />,
                    href: `${basePath}/${pathSlug}/hosts`,
                },
            ],
        },
        communication: {
            title: "Communication",
            items: [
                {
                    name: "Auto reminders",
                    icon: <RiAlarmLine />,
                    href: `${basePath}/${pathSlug}/auto-reminders`,
                },
                {
                    name: "Send messages",
                    icon: <RiMailLine />,
                    href: `${basePath}/${pathSlug}/send-messages`,
                },
            ],
        },
        promotion_and_checkin: {
            title: "Promotion & Check-in",
            titleHref: `${basePath}/${pathSlug}/promotion`,
            items: [
                {
                    name: "Share",
                    icon: <RiShareLine />,

                    onClick: async () => {
                        try {
                            const url = await getShortLink(
                                `${APP_URL}/events/${slug}`,
                            );
                            if (!isDesktop && isNavigatorShareAvailable) {
                                navigator.share({
                                    text: `Join me at `,
                                    url,
                                });
                            } else {
                                setShareModalOpen(true);
                            }
                        } catch (e) {
                            console.error(e);
                        }
                    },
                },
                {
                    name: "Check-in / Event Flyer",
                    icon: <RiQrCodeLine />,
                    href: `${basePath}/${pathSlug}/promotion`,
                    disabled: !slug,
                },
            ],
        },
    };

    const [searchTerm, setSearchTerm] = useState("");
    const flattenedItems = useMemo(() => {
        return Object.values(SIDEBAR_ITEMS).flatMap((section) =>
            section.items.map((item) => ({ ...item, section: section.title })),
        );
    }, [SIDEBAR_ITEMS]);

    const fuse = useMemo(
        () =>
            new Fuse(flattenedItems, {
                keys: ["name", "section"],
                threshold: 0.4,
            }),
        [flattenedItems],
    );

    const filteredItems = useMemo(() => {
        if (!searchTerm) return SIDEBAR_ITEMS;

        const searchResults = fuse.search(searchTerm);
        const groupedResults = searchResults.reduce(
            (acc: SidebarItems, { item }) => {
                if (!acc[item.section]) {
                    acc[item.section] = { title: item.section, items: [] };
                }

                acc[item.section]?.items.push(item);

                return acc;
            },
            {},
        );

        return groupedResults;
    }, [searchTerm, SIDEBAR_ITEMS, fuse]);

    return !isSidebarVisible ? null : (
        <div className={cn("h-screen w-full pr-1")}>
            <motion.div
                ref={sidebarRef}
                initial={{ x: "-100%", z: 10 }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                }}
                onClick={() => {
                    setSideBarState("closed");
                }}
                className="h-[110vh]"
            >
                <div className="absolute z-0 flex h-full w-full max-w-[300px] flex-col bg-black lg:hidden lg:max-h-[90vh]" />

                <div
                    className={cn(
                        "relative z-[100] flex h-full max-h-[85vh] w-full max-w-[300px] flex-col overflow-auto rounded-[10px] bg-v3-card-gray pt-5 lg:max-h-[100vh] lg:max-w-full lg:overflow-hidden lg:pt-0",
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-4">
                        <div className="relative">
                            <Input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                prefixComponent={
                                    <RiSearchLine className="text-gray-400" />
                                }
                            />
                        </div>
                    </div>

                    {Object.entries(filteredItems).map(([key, section]) => (
                        <SubNavigationGroup
                            key={key}
                            name={section.title}
                            titleHref={section.titleHref}
                            items={section.items}
                        >
                            {key === "event" && (
                                <EventJourneyStatusDropdown
                                    eventSlug={slug as string}
                                    permissions={permissions}
                                    setSideBarState={setSideBarState}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                />
                            )}
                        </SubNavigationGroup>
                    ))}
                </div>
            </motion.div>
        </div>
    );
};

export enum ESidebarItems {
    DEFAULT = "",

    SETTINGS = "settings",
    DIGITAL_BADGE = "digital-badge",
    DUPLICATE = "duplicate",
    IMAGE_OVERLAY = "image-overlay",

    ATTENDEES = "attendees",
    HOSTS = "hosts",

    AUTO_REMINDERS = "auto-reminders",
    SEND_MESSAGES = "send-messages",

    SHARE = "share",
    PRINT_WELCOME_SIGN = "print-welcome-sign",
    CHECK_IN_ATTENDEES = "check-in-attendees",
}

export default EventViewSidebar;
